import React from 'react';
import './CombatPanel.css';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {getReadableInkColor} from '../../../utils/colors/getReadableInkColor';
import {checkIfColorBlendsWithStone} from '../../../utils/gameUi/checkIfColorBlendsWithStone';
import {getUnitIconClassNameForButton} from '../../../utils/unit/getUnitIconClassNameForButton';
import {getUnitIconDisplayProps} from '../../../utils/factions/succubes/getUnitIconDisplayProps';
import {getPlayerById} from '../../../utils/players/getPlayerById';
import {getNeutralPlayerMock} from '../../../utils/players/getNeutralPlayerMock';
import {joinClassNames} from '../../gameUi/joinClassNames';
import {MaskedIcon} from '../../coloredIcons/MaskedIcon/MaskedIcon';
import UnitIcon from '../NewUnitToolbar/UnitIcon/UnitIcon';
import RollingValue from '../NewUnitToolbar/RecruitUnitCard/RollingValue';

const DAMAGE_ICON = '/img/icons/damage.png';

// До скольких целей они стоят одним рядом: больше не уместилось бы на телефоне
const TARGETS_IN_ROW_MAX = 3;

/**
 * Панель результата действия отряда: ближний бой, стрельба, способность. Единая раскладка:
 * слева участники (CombatDuel), справа расчет (CombatEquation) и итог (CombatTotal), под
 * ними вывод и кнопки. Вне десктопа итог стоит рядом с участниками, а расчет — под ними.
 * Смена отряда перезапускает появление (key на месте использования)
 *
 * @param {string} toneColor - цвет действия: итог, волна от участников, подписи
 * @param {React.ReactNode} duel - участники
 * @param {React.ReactNode} children - части расчета, раскладку задает CSS по их классам
 */
export const CombatPanel = ({toneColor, duel, className, children, ...rest}) => (
  <div className={joinClassNames('combat-panel', className)} style={{'--combat-tone': toneColor, '--combat-total-ink': getReadableInkColor(toneColor)}} {...rest}>
    <span className={'combat-panel_sweep'} aria-hidden={true}/>
    {duel}
    <span className={'combat-panel_divider'} aria-hidden={true}/>
    {children}
  </div>
);

/**
 * Жетон участника: каменный гекс в рамке цвета владельца
 *
 * @param {string} playerColor - цвет рамки; без него рамка нейтральная
 * @param {boolean} [isSmall] - рядом несколько целей
 * @param {boolean} [isMini] - целей так много, что они стоят в два ряда
 * @param {boolean} [isTarget] - приезжает с другой стороны, чем отряд-участник
 * @param {React.ReactNode} [plaque] - плашка на нижней грани
 */
export const CombatToken = ({playerColor, isSmall, isMini, isTarget, plaque, title, children}) => (
  <div
    className={joinClassNames(
      'combat-token',
      isSmall && 'combat-token_small',
      isMini && 'combat-token_mini',
      isTarget && 'combat-token_target',
    )}
    style={getPlayerColorStyle(playerColor)}
    title={title}
  >
    <span className={'combat-token_glow'} aria-hidden={true}/>
    <div className={'combat-token_body'}>{children}</div>
    {plaque !== undefined && plaque !== null && <span className={'combat-token_plaque'}>{plaque}</span>}
  </div>
);

/** Силуэт отряда в жетоне участника. iconProps — как у UnitIcon: цвет, фракция, скин */
export const CombatUnitToken = ({unitType, iconProps, ...tokenProps}) => (
  <CombatToken playerColor={iconProps.playerColor} {...tokenProps}>
    <div className={checkIfColorBlendsWithStone(iconProps.playerColor) ? 'combat-token_outline' : undefined}>
      <UnitIcon {...iconProps} unitType={unitType} className={getUnitIconClassNameForButton(unitType)}/>
    </div>
  </CombatToken>
);

/**
 * Как отряд выглядит в панели: по его нынешнему владельцу и с половиной цвета исходного
 * у соблазненного, как на поле. Нейтральный отряд среди игроков не найдется
 */
export const getCombatUnitIconProps = (unit, players) => {
  const owner = getPlayerById(players, unit.ownerId) || getNeutralPlayerMock();
  return getUnitIconDisplayProps(unit, owner, players);
};

/**
 * Участники: отряд, значок действия и цели
 *
 * @param {React.ReactNode} actor
 * @param {React.ReactNode} actionIcon - значок между ними, с волной при появлении
 * @param {React.ReactNode[]} [targets]
 */
export const CombatDuel = ({actor, actionIcon, targets = []}) => (
  <div className={'combat-duel'}>
    {actor}
    <span className={'combat-duel_action'}>
      <span className={'combat-duel_impact'} aria-hidden={true}/>
      {actionIcon}
    </span>
    {targets.length > 0 && (
      <div className={joinClassNames('combat-duel_targets', targets.length > TARGETS_IN_ROW_MAX && 'combat-duel_targets_grid')}>
        {targets}
      </div>
    )}
  </div>
);

/** Как рисовать цели: одна — крупно, как отряд; несколько — мельче, чтобы панель не росла */
export const getCombatTargetSizeProps = (targetCount) => ({
  isSmall: targetCount > 1 && targetCount <= TARGETS_IN_ROW_MAX,
  isMini: targetCount > TARGETS_IN_ROW_MAX,
});

/** Урон цели — на плашке жетона */
export const CombatDamagePlaque = ({damage}) => (
  <>
    <RollingValue value={damage} rank={damage}/>
    <MaskedIcon className={'combat-token_plaque-icon'} src={DAMAGE_ICON} alt={'урон'}/>
  </>
);

/** Строка расчета: слагаемые появляются по очереди */
export const CombatEquation = ({className, children}) => (
  <div className={joinClassNames('combat-equation', className)}>{children}</div>
);

/**
 * Слагаемое: значение со значком в темном углублении
 *
 * @param {string|number} value
 * @param {string} [icon] - путь к одноцветному значку
 * @param {string} [color] - цвет значения и значка
 * @param {string} [caption] - подпись под значением
 */
export const CombatTerm = ({value, icon, iconAlt, color, caption, title}) => (
  <div className={'combat-term'} style={color ? {color} : undefined} title={title}>
    <span className={'combat-term_main'}>
      <RollingValue value={value} rank={Number(value) || 0}/>
      {icon && <MaskedIcon className={'combat-term_icon'} src={icon} alt={iconAlt} color={color}/>}
    </span>
    {caption && <span className={'combat-term_caption'}>{caption}</span>}
  </div>
);

/** Знак между слагаемыми */
export const CombatOperator = ({children}) => <span className={'combat-operator'}>{children}</span>;

/**
 * Кости одной группой: подпись сверху говорит, какие из них идут в расчет
 *
 * @param {string} [caption]
 */
export const CombatDiceGroup = ({caption, title, children}) => (
  <div className={'combat-dice-group'} title={title}>
    {caption && <span className={'combat-dice-group_caption'}>{caption}</span>}
    <div className={'combat-dice-group_dice'}>{children}</div>
  </div>
);

/**
 * Итог расчета: эмаль цвета действия. Смена значения катит его и дает вспышку
 *
 * @param {number} value
 * @param {string} [label]
 * @param {string} [hint] - условие успеха
 * @param {boolean} [isDamage] - значок урона после значения
 */
export const CombatTotal = ({value, label = 'Итого', hint, isDamage}) => (
  <div className={'combat-total'}>
    <span key={`flash-${value}`} className={'combat-total_flash'} aria-hidden={true}/>
    <span className={'combat-total_label'}>{label}</span>
    <span className={'combat-total_value'}>
      <RollingValue value={value} rank={value}/>
      {isDamage && <MaskedIcon className={'combat-total_icon'} src={DAMAGE_ICON} alt={'урон'}/>}
    </span>
    {hint && <span className={'combat-total_hint'}>{hint}</span>}
  </div>
);

/** Название способности над расчетом */
export const CombatName = ({children}) => <div className={'combat-name'}>{children}</div>;

/**
 * Вывод: ложится печатью
 *
 * @param {boolean} [isSuccess]
 */
export const CombatVerdict = ({isSuccess, children}) => (
  <div className={joinClassNames('combat-verdict', isSuccess ? 'combat-verdict_success' : 'combat-verdict_fail')}>
    {children}
  </div>
);

/** Кнопки под расчетом */
export const CombatControls = ({children}) => <div className={'combat-controls'}>{children}</div>;
